import React, {
  createContext,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { FeatureFlagManager } from './FeatureFlagManager';
import { FeatureFlagContextValue, FeatureFlagState } from './types';

/**
 * Feature Flag Context
 */
export const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(
  null,
);

interface FeatureFlagProviderProps {
  children: ReactNode;
  initialFlags?: FeatureFlagState;
}

/**
 * Feature Flag Provider Component
 * Provides feature flag state and controls to the entire app
 */
export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({
  children,
  initialFlags,
}) => {
  const [flags, setFlags] = useState<FeatureFlagState>({});
  const [manager] = useState(() => FeatureFlagManager.getInstance());

  // Initialize flags
  useEffect(() => {
    // Set initial flags if provided
    if (initialFlags) {
      Object.entries(initialFlags).forEach(([key, value]) => {
        manager.setFlag(key, value);
      });
    }

    // Get current flags from manager
    setFlags(manager.getAllFlags());

    // Subscribe to flag changes
    const unsubscribe = manager.subscribe((newFlags) => {
      setFlags(newFlags);
    });

    return unsubscribe;
  }, [manager, initialFlags]);

  // Context value methods
  const isEnabled = useCallback(
    (flagKey: string): boolean => {
      return manager.isEnabled(flagKey);
    },
    [manager],
  );

  const getFlag = useCallback(
    (flagKey: string): boolean => {
      return manager.getFlag(flagKey);
    },
    [manager],
  );

  const setFlag = useCallback(
    (flagKey: string, value: boolean): void => {
      manager.setFlag(flagKey, value);
    },
    [manager],
  );

  const resetFlag = useCallback(
    (flagKey: string): void => {
      manager.resetFlag(flagKey);
    },
    [manager],
  );

  const resetAllFlags = useCallback((): void => {
    manager.resetAllFlags();
  }, [manager]);

  const contextValue: FeatureFlagContextValue = {
    flags,
    isEnabled,
    getFlag,
    setFlag,
    resetFlag,
    resetAllFlags,
  };

  return (
    <FeatureFlagContext.Provider value={contextValue}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

/**
 * HOC to wrap components with feature flag provider
 */
export const withFeatureFlags = <P extends object>(
  Component: React.ComponentType<P>,
  initialFlags?: FeatureFlagState,
) => {
  const WrappedComponent: React.FC<P> = (props) => (
    <FeatureFlagProvider initialFlags={initialFlags}>
      <Component {...props} />
    </FeatureFlagProvider>
  );

  WrappedComponent.displayName = `withFeatureFlags(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

/**
 * Component for conditional rendering based on feature flags
 */
interface FeatureFlagGateProps {
  flag: string;
  children: ReactNode;
  fallback?: ReactNode;
  invert?: boolean;
}

export const FeatureFlagGate: React.FC<FeatureFlagGateProps> = ({
  flag,
  children,
  fallback = null,
  invert = false,
}) => {
  const [isEnabled, setIsEnabled] = useState(false);
  const [manager] = useState(() => FeatureFlagManager.getInstance());

  useEffect(() => {
    // Get initial value
    setIsEnabled(manager.isEnabled(flag));

    // Subscribe to changes
    const unsubscribe = manager.subscribe((flags) => {
      setIsEnabled(flags[flag] ?? false);
    });

    return unsubscribe;
  }, [flag, manager]);

  const shouldRender = invert ? !isEnabled : isEnabled;

  return <>{shouldRender ? children : fallback}</>;
};

/**
 * Component for rendering different content based on multiple flags
 */
interface FeatureFlagSwitchProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface FeatureFlagCaseProps {
  flag: string;
  value?: boolean;
  children: ReactNode;
}

export const FeatureFlagCase: React.FC<FeatureFlagCaseProps> = ({
  children,
}) => {
  return <>{children}</>;
};

export const FeatureFlagSwitch: React.FC<FeatureFlagSwitchProps> = ({
  children,
  fallback = null,
}) => {
  const [flags, setFlags] = useState<FeatureFlagState>({});
  const [manager] = useState(() => FeatureFlagManager.getInstance());

  useEffect(() => {
    setFlags(manager.getAllFlags());

    return manager.subscribe((newFlags) => {
      setFlags(newFlags);
    });
  }, [manager]);

  // Find the first matching case
  const childrenArray = React.Children.toArray(children);

  for (const child of childrenArray) {
    if (React.isValidElement(child) && child.type === FeatureFlagCase) {
      const { flag, value = true } = child.props as FeatureFlagCaseProps;
      const flagValue = flags[flag] ?? false;

      if (flagValue === value) {
        return <>{child}</>;
      }
    }
  }

  return <>{fallback}</>;
};
